import { useState } from 'react'
import Asset from '@/components/ui/asset'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import Tag from '@/components/ui/tag'
import FilterMenu from '@/components/generals/filter-menu'
import DetailSection from '@/components/companies/detail/detail-section'
import PipelineHealth from '@/components/companies/detail/pipeline-health'
import ActivityTrend from '@/components/companies/detail/activity-trend'
import ScoreCard from '@/components/companies/detail/score-card'
import CompanyRelations from '@/components/companies/detail/company-relations'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useCrmStore } from '@/stores/crm-store-provider'
import BuildingIcon from '@/assets/images/companies/detail/building.svg'
import XIcon from '@/assets/images/companies/detail/x.svg'
import MailIcon from '@/assets/images/companies/detail/mail-04.svg'
import PhoneIcon from '@/assets/images/companies/detail/phone.svg'

import {
    SCORE_CARDS,
    TREND_WINDOWS,
    ownerByName,
    tagTone
} from '@/constants/companies'

import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

const WINDOW_OPTIONS = TREND_WINDOWS.map(label => ({ value: label, label }))

export default function CompanyDetail() {
    const detailId = useCrmStore(state => state.detailId)
    const detailOpen = useCrmStore(state => state.detailOpen)
    const companies = useCrmStore(state => state.companies)
    const closeDetail = useCrmStore(state => state.closeDetail)
    const openProfile = useCrmStore(state => state.openProfile)
    const [trendWindow, setTrendWindow] = useState(TREND_WINDOWS[1])
    const [scoreWindow, setScoreWindow] = useState(TREND_WINDOWS[1])

    const company = companies.find(item => item.id === detailId)
    const owner = company ? ownerByName(company.owner) : null

    return (
        <Sheet
            open={detailOpen && company !== undefined}
            onOpenChange={open => !open && closeDetail()}
        >
            <SheetContent side='right' className='sm:w-[560px] sm:max-w-[560px]'>
                <SheetHeader>
                    <div className='flex items-center gap-2'>
                        <BuildingIcon aria-hidden className='text-icon size-3.5' />
                        <SheetTitle>Companies Detail</SheetTitle>
                    </div>
                    <SheetDescription className='sr-only'>
                        Account summary, pipeline health, activity and score cards
                    </SheetDescription>
                    <SheetClose asChild>
                        <Button
                            variant='ghost'
                            size='icon-sm'
                            className='-mr-1'
                            aria-label="Close details"
                        >
                            <XIcon aria-hidden className='text-foreground size-4' />
                        </Button>
                    </SheetClose>
                </SheetHeader>

                {company && owner && (
                    <ScrollArea className='min-h-0 flex-1'>
                        <div className='flex items-start gap-3 p-5 shadow-[inset_0_-1px_0_var(--line-strong)]'>
                            <span className='bg-muted flex size-[50px] shrink-0 items-center justify-center rounded-[12.5px] shadow-[0px_6.25px_6.25px_0px_rgba(15,15,15,0.24),0px_0px_0px_1.563px_#232323]'>
                                {company.logo ? (
                                    <Asset
                                        src={company.logo}
                                        alt={company.logoAlt ?? `${company.name} logo`}
                                        width={1}
                                        height={1}
                                        fit='contain'
                                        fallback={(
                                            <span className='h2-style text-soft'>
                                                {company.name.slice(0, 1)}
                                            </span>
                                        )}
                                        className='size-8'
                                    />
                                ) : (
                                    <span className='h2-style text-soft'>
                                        {company.name.slice(0, 1)}
                                    </span>
                                )}
                            </span>
                            <div className='flex min-w-0 flex-col gap-3'>
                                <h2 className='truncate' data-mdlfy-cms={`companies:${company.id}:name`}>
                                    {company.name}
                                </h2>
                                <div className='flex flex-wrap items-center gap-[3px]'>
                                    {company.tags.map(tag => (
                                        <Tag key={tag} tone={tagTone(tag)} size='sm'>
                                            {tag}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <DetailSection title="Account summary">
                            <div className='lead-style flex flex-wrap items-center gap-x-4 gap-y-3'>
                                <Button
                                    variant='ghost'
                                    size='none'
                                    onClick={() => openProfile(owner.name)}
                                    aria-label={`Open ${owner.name} profile`}
                                    className='lead-style text-foreground -mx-1.5 gap-1.5 px-1.5 py-1 font-medium'
                                >
                                    <Avatar src={owner.avatar} alt='' />
                                    <span data-mdlfy-cms={`companies:${company.id}:owner`}>
                                        {company.owner}
                                    </span>
                                </Button>
                                {owner.email && (
                                    <span className='flex items-center gap-1'>
                                        <MailIcon aria-hidden className='text-soft size-3' />
                                        {owner.email}
                                    </span>
                                )}
                                {owner.phone && (
                                    <span className='flex items-center gap-1'>
                                        <PhoneIcon aria-hidden className='text-soft size-3' />
                                        {owner.phone}
                                    </span>
                                )}
                            </div>
                        </DetailSection>

                        <CompanyRelations companyId={company.id} />

                        <DetailSection title="Pipeline health">
                            <PipelineHealth company={company} />
                        </DetailSection>

                        <DetailSection
                            title="Activity trend"
                            action={(
                                <FilterMenu
                                    value={trendWindow}
                                    options={WINDOW_OPTIONS}
                                    onChange={setTrendWindow}
                                    align='end'
                                />
                            )}
                        >
                            <ActivityTrend company={company} />
                        </DetailSection>

                        <DetailSection
                            title="Score card"
                            className='gap-3 shadow-none'
                            action={(
                                <FilterMenu
                                    value={scoreWindow}
                                    options={WINDOW_OPTIONS}
                                    onChange={setScoreWindow}
                                    align='end'
                                    className='shadow-[0px_4px_4px_0px_rgba(15,15,15,0.24),0px_0px_0px_1px_#393939]'
                                />
                            )}
                        >
                            <div className='flex flex-col gap-2'>
                                {SCORE_CARDS.map((card, index) => (
                                    <ScoreCard key={`${card.title}-${index}`} card={card} />
                                ))}
                            </div>
                        </DetailSection>
                    </ScrollArea>
                )}

                <SheetFooter>
                    <Button variant='link' size='none' href='/help' className='lead-style'>
                        Need help? Ask us.
                    </Button>
                    <div className='flex items-center gap-2'>
                        <SheetClose asChild>
                            <Button variant='subtle' size='sm'>
                                Cancel
                            </Button>
                        </SheetClose>
                        <Button variant='primary' size='sm' onClick={closeDetail}>
                            Save Update
                        </Button>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}
